import { makeAutoObservable, runInAction } from 'mobx';
import { IContainer } from '@/shared/config/Container';
import { PaymentMethodType } from '@/core/domain/services/IPaymentService';
import type { paymentMethodTypeSchema } from '@/app/(unauth)/order/[orderId]/pay/_components/PaymentMethod';
import type z from 'zod';
import { ApiPaymentService, type PaymentInitializationParams } from '@/core/infrastructure/services/ApiPaymentService';

export interface PaymentMethodsType {
  id: string;
  name: string;
  type: PaymentMethodType;
  icon: string;
  isAvailable: boolean;
  minimumAmount?: number;
  maximumAmount?: number;
  processingFee?: number;
}

export interface PaymentTransaction {
  id: string;
  orderId: string;
  installmentId?: string;
  amount: number;
  method: PaymentMethodType;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  reference?: string;
  phoneNumber?: string;
  createdAt: Date;
  updatedAt: Date;
  failureReason?: string;
  details: PaymentInitiationRequest,
  provider: string;
  instructions?: string
}

export interface PaymentInitiationRequest {
  amount: number;
  orderId: string;
  installmentId?: string;
  phoneNumber: string;
  method: PaymentMethodType;
  provider: string;
  idempotencyKey: string;
  autoDebitAuthorized?: boolean;
}

export interface PaymentVerificationRequest {
  transactionId: string;
  reference: string;
  method: PaymentMethodType;
}

export interface PaymentResponse {
  success: boolean;
  transactionId?: string;
  reference?: string;
  message: string;
  redirectUrl?: string;
  pollInterval?: number;
}

class PaymentStore {
  // Observable state
  isLoading = false;
  error: string | null = null;

  // Payment data
  availablePaymentMethodsTypes: PaymentMethodType[] = [];
  currentTransaction: PaymentTransaction | null = null;
  transactionHistory: PaymentTransaction[] = [];

  // Processing state
  isInitializingPayment = false;
  isVerifyingPayment = false;
  verificationAttempts = 0;
  maxVerificationAttempts = 10;
  verificationInterval: NodeJS.Timeout | null = null;
  verificationDeadline: number | null = null;

  // Telco mandate state
  /** Backend-provided flag indicating if user needs to approve on phone. Null if backend doesn't provide this. */
  serverRequiresUserApproval: boolean | null = null;
  /** Whether the countdown timer has expired (user can now manually verify) */
  timerExpired = false;

  private readonly checkoutStore = this.container.checkoutStore;

  constructor(private container: IContainer,  private paymentService: ApiPaymentService) {
    makeAutoObservable(this);
  }

  // Computed properties
  get hasPendingTransaction(): boolean {
    return this.currentTransaction?.status === 'pending' ||
      this.currentTransaction?.status === 'processing';
  }

  get canRetryPayment(): boolean {
    return this.currentTransaction?.status === 'failed' &&
      !this.isLoading;
  }

  get isPaymentInProgress(): boolean {
    return this.isInitializingPayment ||
      this.isVerifyingPayment ||
      this.hasPendingTransaction;
  }

  /**
   * Determines if user needs to manually approve the payment on their phone.
   * Uses backend value if available, otherwise falls back to heuristic based on auto_debit.enabled.
   *
   * - true: User needs to approve on phone (show countdown + button)
   * - false: Auto-debit will happen (show simple spinner)
   */
  get requiresUserApproval(): boolean {
    // If backend provides explicit field, use it
    if (this.serverRequiresUserApproval !== null) {
      return this.serverRequiresUserApproval;
    }
    // Fallback heuristic: if auto_debit not enabled, user needs to approve
    const checkoutSession = this.checkoutStore.currentCheckoutSession;
    const autoDebitEnabled = (checkoutSession as any)?.auto_debit?.enabled === true;
    return !autoDebitEnabled;
  }

  // Actions
  setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  setError(error: string | null): void {
    this.error = error;
  }

  setInitializingPayment(initializing: boolean): void {
    this.isInitializingPayment = initializing;
  }

  setVerifyingPayment(verifying: boolean): void {
    this.isVerifyingPayment = verifying;
  }

  setTimerExpired(expired: boolean): void {
    this.timerExpired = expired;
  }

  setServerRequiresUserApproval(requires: boolean | null): void {
    this.serverRequiresUserApproval = requires;
  }

  cacheTransaction(request: PaymentInitiationRequest){
    runInAction(() => {
      this.currentTransaction = {
        id: 'txn_' + Math.random().toString(36).substring(2, 15),
        orderId: request.orderId,
        installmentId: request.installmentId,
        amount: request.amount,
        method: request.method,
        status: 'pending',
        phoneNumber: request.phoneNumber,
        createdAt: new Date(),
        updatedAt: new Date(),
        details: request,
        provider: request.provider
      }
    });
  }

  // MTN Mobile Money Payment
  async initializeMobileMoneyPayment(request: PaymentInitiationRequest): Promise<PaymentResponse> {
    try {
      this.setInitializingPayment(true);
      this.setError(null);
      this.setServerRequiresUserApproval(null);
      this.setTimerExpired(false);
      // Create transaction
      const transaction = this.createTransaction(request);

      const response = await this.paymentService.initializePayment(transaction)

      if (response) {
        this.updateTransaction({
          status: 'processing',
          reference: response.paymentId,
        });

        // Store backend-provided requiresUserApproval if available
        if (response.requiresUserApproval !== undefined) {
          this.setServerRequiresUserApproval(response.requiresUserApproval);
        }

        // Start verification polling
        this.startPaymentVerification(
          transaction.idempotencyKey || transaction.provider,
          response.paymentId,
          PaymentMethodType.MOBILE_MONEY,
          {
            pollIntervalMs: Math.max((response.pollingInterval || 5) * 1000, 1000),
            timeoutSeconds: Math.max(response.timeoutSeconds || 300, 30),
          }
        );
        this.updateTransaction({
          instructions: response.instructions.message ?? ''
        })
        return {
          success: true,
          transactionId: transaction.idempotencyKey,
          reference: response.paymentId,
          message: 'Payment initiated successfully. Please complete the payment on your phone.',
          pollInterval: Math.max((response.pollingInterval || 5) * 1000, 1000),
        };
      } else {
        this.updateTransaction({
          status: 'failed',
          failureReason: 'Payment initialization failed',
        });

        throw new Error('Payment initialization failed');
      }
    } catch (error: any) {
      this.updateTransaction({
        status: 'failed',
        failureReason: error.message,
      });

      runInAction(() => {
        this.setError(error.message || 'Failed to initialize MTN payment');
      });

      return {
        success: false,
        message: error.message || 'Failed to initialize MTN payment',
      };
    } finally {
      this.setInitializingPayment(false);
    }
  }


//   {
//   "intent": "checkout",
//     "context": {
//     "checkout_session_id": "d80dfedc-9574-4f21-92d9-928c130fef55",
//       "order_id": "d80dfedc-9574-4f21-92d9-928c130fef55",
//         "installment_id": "d80dfedc-9574-4f21-92d9-928c130fef55",
//           "amount": "935"
//   },
//   "payment_provider": "mtn",
//     "payment_method": "mobile_money",
//       "phone_number": "+233553379296",
//         "idempotency_key": "1761578731783-o775eyfij"
// }

  async registerPayment(
    request: z.infer<typeof paymentMethodTypeSchema> & { autoDebitAuthorized?: boolean },
    amount: number,
    orderId: string,
    installmentId?: string
  ) {
    this.setServerRequiresUserApproval(null);
    this.setTimerExpired(false);
    const paymentOptionType = String(request.mobileNetwork || '').trim().toLowerCase();
    const provider = this.mapPaymentOptionTypeToProvider(paymentOptionType);
    const idempotencyKey = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    if (!provider) {
      throw new Error('Unsupported payment option');
    }

    const payload: PaymentInitiationRequest = {
      amount: Number(amount),
      orderId,
      installmentId,
      phoneNumber: request.phoneNumber,
      method: request.type as PaymentMethodType,
      provider,
      idempotencyKey,
      autoDebitAuthorized: request.autoDebitAuthorized ?? true, // Default to true for PSS
    }
    const transaction = this.createTransaction(payload);

    runInAction(() => {
      this.currentTransaction = {
        id: 'txn_' + Math.random().toString(36).substring(2, 15),
        orderId: payload.orderId,
        installmentId: payload.installmentId,
        amount: payload.amount,
        method: payload.method,
        status: 'pending',
        phoneNumber: payload.phoneNumber,
        createdAt: new Date(),
        updatedAt: new Date(),
        details: payload,
        provider: payload.provider
      }
    });

    return transaction;
}

  // Generic payment initialization
  async initializePayment(): Promise<PaymentResponse> {
    if (!this.currentTransaction) {
      return {
        success: false,
        message: 'No current transaction to initialize payment for',
      };
    }
    switch (this.currentTransaction!.method) {
      case PaymentMethodType.MOBILE_MONEY:
        return this.initializeMobileMoneyPayment(this.currentTransaction!.details);
      case PaymentMethodType.BANK_TRANSFER:
        return this.initializeEcobankPayment({} as PaymentInitiationRequest);
      default:
        return {
          success: false,
          message: 'Unsupported payment method',
        };
    }
  }

  // Ecobank Payment (placeholder for future implementation)
  async initializeEcobankPayment(_request: PaymentInitiationRequest): Promise<PaymentResponse> {
    // TODO: Implement Ecobank payment integration
    return {
      success: false,
      message: 'Ecobank payment not yet implemented',
    };
  }

  // Payment verification
  async verifyPayment(_transactionId: string, reference: string, method: PaymentMethodType): Promise<boolean> {
    try {
      this.setVerifyingPayment(true);

      switch (method) {
        case PaymentMethodType.MOBILE_MONEY:
          const response = await this.paymentService.getPaymentStatusPoll(reference);

          if (response.status === 'completed') {
            this.updateTransaction({
              status: 'completed',
            });
            this.stopPaymentVerification();
            return true;
          }

          if (['failed', 'cancelled', 'refunded'].includes(response.status)) {
            this.updateTransaction({
              status: 'failed',
              failureReason: response.statusDisplay || 'Payment failed',
            });
            this.stopPaymentVerification();
            return true;
          }

          // Payment still in progress, continue polling.
          return false;
        default:
          throw new Error('Unsupported payment method for verification');
      }
    } catch (error: any) {
      this.setError(error?.message ? String(error.message) : 'Payment verification failed');
      return false;
    } finally {
      this.setVerifyingPayment(false);
    }
  }

  // Stop payment verification
  stopPaymentVerification(): void {
    if (this.verificationInterval) {
      clearInterval(this.verificationInterval);
      this.verificationInterval = null;
    }
    this.verificationAttempts = 0;
    this.verificationDeadline = null;
  }

  // Manual payment verification trigger
  async manualVerifyPayment(): Promise<boolean> {
    if (!this.currentTransaction?.reference) {
      throw new Error('No transaction reference available');
    }

    return this.verifyPayment(
      this.currentTransaction.id,
      this.currentTransaction.reference,
      this.currentTransaction.method
    );
  }

  // Cancel payment
  cancelPayment(): void {
    this.stopPaymentVerification();

    if (this.currentTransaction) {
      this.updateTransaction({
        status: 'cancelled',
      });
    }
  }

  // Get transaction by ID
  getTransaction(transactionId: string): PaymentTransaction | undefined {
    return this.transactionHistory.find(t => t.id === transactionId);
  }

  // Get transactions by order
  getTransactionsByOrder(orderId: string): PaymentTransaction[] {
    return this.transactionHistory.filter(t => t.orderId === orderId);
  }

  // Clear current transaction
  clearCurrentTransaction(): void {
    this.stopPaymentVerification();
    runInAction(() => {
      this.currentTransaction = null;
      this.error = null;
      this.verificationAttempts = 0;
    });
  }

  // Reset store
  reset(): void {
    this.stopPaymentVerification();
    runInAction(() => {
      this.isLoading = false;
      this.error = null;
      this.currentTransaction = null;
      this.transactionHistory = [];
      this.isInitializingPayment = false;
      this.isVerifyingPayment = false;
      this.verificationAttempts = 0;
      this.serverRequiresUserApproval = null;
      this.timerExpired = false;
    });
  }

  // Create new transaction
  private createTransaction(request: PaymentInitiationRequest): PaymentInitializationParams {
    const checkoutSessionId = this.checkoutStore.currentCheckoutSession?.session_id;
    const isCheckoutPayment = Boolean(checkoutSessionId) && !request.installmentId;

    const transaction: PaymentInitializationParams = {
      provider: request.provider,
      method: request.method,
      phoneNumber: request.phoneNumber,
      intent: isCheckoutPayment ? 'checkout' : 'installment',
      autoDebitAuthorized: request.autoDebitAuthorized ?? true, // Always true for PSS
      context: {
        ...(isCheckoutPayment
          ? { checkout_session_id: checkoutSessionId }
          : { order_id: request.orderId, installment_id: request.installmentId }),
        amount: `${request.amount}`,
      },
      idempotencyKey: request.idempotencyKey,
    };

    return transaction;
  }

  private mapPaymentOptionTypeToProvider(optionType: string): string | null {
    switch (optionType) {
      case 'mtn_mobile_money':
        return 'mtn';
      case 'telecel_mobile_money':
        return 'telecel';
      case 'debit_card':
        // Card is processed via Paystack but is intentionally not exposed in the current UI flow.
        return null;
      default:
        return null;
    }
  }

  // Update transaction
  private updateTransaction(updates: Partial<PaymentTransaction>): void {
    if (!this.currentTransaction) return;

    runInAction(() => {
      if (this.currentTransaction) {
        Object.assign(this.currentTransaction, {
          ...updates,
          updatedAt: new Date(),
        });

        // Update in history as well
        const historyIndex = this.transactionHistory.findIndex(
          t => t.id === this.currentTransaction!.id
        );
        if (historyIndex >= 0) {
          Object.assign(this.transactionHistory![historyIndex]!, {
            ...updates,
            updatedAt: new Date(),
          });
        }
      }
    });
  }

  // Start automatic payment verification
  private startPaymentVerification(
    transactionId: string,
    reference: string,
    method: PaymentMethodType,
    policy: { pollIntervalMs: number; timeoutSeconds: number } = {
      pollIntervalMs: 3000,
      timeoutSeconds: 300,
    }
  ): void {
    this.startPaymentVerificationWithPolicy(transactionId, reference, method, policy);
  }

  private startPaymentVerificationWithPolicy(
    transactionId: string,
    reference: string,
    method: PaymentMethodType,
    policy: { pollIntervalMs: number; timeoutSeconds: number }
  ): void {
    const pollIntervalMs = Math.max(policy.pollIntervalMs, 1000);
    const timeoutSeconds = Math.max(policy.timeoutSeconds, 30);

    this.verificationAttempts = 0;
    this.maxVerificationAttempts = Math.max(
      Math.ceil((timeoutSeconds * 1000) / pollIntervalMs),
      1,
    );
    this.verificationDeadline = Date.now() + timeoutSeconds * 1000;

    this.verificationInterval = setInterval(async () => {
      this.verificationAttempts++;

      if (this.verificationDeadline && Date.now() > this.verificationDeadline) {
        this.updateTransaction({
          status: 'failed',
          failureReason: 'Payment verification timeout',
        });
        this.stopPaymentVerification();
        return;
      }

      const isTerminal = await this.verifyPayment(transactionId, reference, method);

      if (isTerminal) {
        // verifyPayment() already updates state + stops polling for terminal states
        return;
      }

      if (this.verificationAttempts >= this.maxVerificationAttempts) {
        this.updateTransaction({
          status: 'failed',
          failureReason: 'Payment verification timeout',
        });
        this.stopPaymentVerification();
      }
    }, pollIntervalMs);
  }

  // Validation
  private validatePaymentRequest(request: PaymentInitiationRequest): { isValid: boolean; message: string } {
    if (!request.amount || request.amount <= 0) {
      return { isValid: false, message: 'Invalid payment amount' };
    }

    if (!request.orderId) {
      return { isValid: false, message: 'Order ID is required' };
    }

    if (!request.phoneNumber) {
      return { isValid: false, message: 'Phone number is required' };
    }

    // Validate phone number format (Ghana)
    const phoneRegex = /^(\+233|0)(2[0-9]|5[0-4])[0-9]{7}$/;
    if (!phoneRegex.test(request.phoneNumber)) {
      return { isValid: false, message: 'Invalid phone number format' };
    }

    return { isValid: true, message: '' };
  }
}

export { PaymentStore };
